<template>
  <f7-page name="save-to-buy-home">
    <f7-navbar :title="$t('saveToBuy.title')" back-link />

    <div class="save-to-buy-page p-4 space-y-4 max-w-lg mx-auto">
      <!-- Top Hero Banner -->
      <f7-card class="hero-card" padding="false">
        <f7-card-content class="hero-content">
          <div class="hero-ambient"></div>

          <div class="relative z-10 space-y-2">
            <div class="display-flex align-items-center gap-1.5">
              <span class="hero-badge">
                <f7-icon material="handshake" :size="15" />
              </span>
              <span class="font-label-md font-semibold">{{ $t('saveToBuy.heroCoopLabel') }}</span>
            </div>
            <h1 class="font-headline-sm font-bold">{{ $t('saveToBuy.heroTitle') }}</h1>
            <p class="hero-desc font-body-sm text-on-surface-variant leading-relaxed">
              {{ $t('saveToBuy.heroDesc') }}
            </p>
            <div class="pt-1 display-flex align-items-center gap-2">
              <f7-button fill raised round  @click="goDemand" class="hero-cta">
                <f7-icon material="add_circle" :size="18" />
                {{ $t('saveToBuy.proposeNeed') }}
              </f7-button>
            </div>
          </div>
        </f7-card-content>
      </f7-card>

      <!-- Active Campaigns Section -->
      <f7-block class="section-block">
        <div class="display-flex align-items-center justify-content-space-between mb-3">
          <div class="display-flex align-items-center gap-1.5">
            <f7-icon material="local_mall" :size="20" class="text-primary" />
            <h2 class="font-headline-sm text-on-surface font-bold text-base">{{ $t('saveToBuy.campaigns') }}</h2>
          </div>
          <span class="font-label-sm text-primary font-semibold display-flex align-items-center gap-1">
            {{ $t('saveToBuy.live') }}
            <span class="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
          </span>
        </div>

        <div v-if="loading" class="text-align-center py-6">
          <f7-preloader color="green" />
        </div>

        <div v-else-if="groupCampaigns.length">
          <f7-list inset strong outline dividers class="campaigns-list">
            <f7-list-item
              v-for="c in groupCampaigns"
              :key="c.id"
              link
              :href="campaignHref(c)"
              class="campaign-item"
            >
              <div class="campaign-item-content">
                <div class="display-flex align-items-flex-start justify-content-space-between gap-2">
                  <div class="min-w-0">
                    <div class="display-flex align-items-center gap-2 mb-1">
                      <span
                        class="status-badge"
                        :style="{ color: statusMeta(c).color, background: statusBg(c) }"
                      >
                        {{ $t(statusMeta(c).labelKey) }}
                      </span>
                    </div>
                    <div class="display-flex align-items-center justify-content-space-between gap-2">
                      <h3 class="font-headline-sm text-on-surface font-bold text-base line-clamp-1">{{ c.title }}</h3>
                      <span class="font-headline-sm text-primary font-bold text-lg whitespace-nowrap">{{ priceLabel(c) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Progress Bar -->
                <div class="space-y-1 mt-2">
                  <div class="display-flex justify-content-space-between align-items-center text-label-sm text-on-surface-variant">
                    <span><strong>{{ c.currentQuantity }}</strong> / {{ c.targetQuantity }} {{ $t('saveToBuy.units') }}</span>
                    <span class="text-primary font-bold">{{ Math.round(progressOf(c)) }}%</span>
                  </div>
                  <f7-progressbar class="campaign-progress" :progress="Math.round(progressOf(c))" />
                </div>
              </div>
            </f7-list-item>
          </f7-list>
        </div>

        <div v-else class="empty-state">
          <f7-icon f7="bag_fill" :size="36" class="text-outline mb-2" />
          <p class="font-body-sm text-on-surface-variant">{{ $t('saveToBuy.noCampaigns') }}</p>
        </div>
      </f7-block>

      <!-- My commitments Section -->
      <f7-block v-if="myCommits.length" class="section-block">
        <div class="display-flex align-items-center justify-content-space-between mb-3">
          <h2 class="font-headline-sm text-on-surface font-bold text-base">{{ $t('saveToBuy.myCampaigns') }}</h2>
        </div>
        <f7-list inset strong outline dividers class="commits-list">
          <f7-list-item
            v-for="mc in myCommits"
            :key="mc.participant.id"
            link
            :href="campaignHref(mc.campaign)"
            class="commit-item"
          >
            <div class="commit-item-content">
              <div class="min-w-0 flex-1">
                <h4 class="font-headline-sm text-on-surface font-bold text-sm truncate">{{ mc.campaign.title }}</h4>
                <p class="font-label-sm text-on-surface-variant mt-0.5">
                  {{ fmtCfa(mc.participant.committedAmount) }} · {{ $t(participantMeta(mc.participant).labelKey) }}
                </p>
              </div>
              <f7-icon material="chevron_right" :size="20" class="text-primary" />
            </div>
          </f7-list-item>
        </f7-list>
      </f7-block>

      <!-- Approved Products Section -->
      <f7-block class="section-block">
        <div class="display-flex align-items-center justify-content-space-between mb-3">
          <h2 class="font-headline-sm text-on-surface font-bold text-base">{{ $t('saveToBuy.approvedProducts') }}</h2>
        </div>

        <div v-if="!eligibleProducts.length" class="empty-state">
          <f7-icon f7="bag_fill" :size="36" class="text-outline mb-2" />
          <p class="font-body-sm text-on-surface-variant mb-3">{{ $t('saveToBuy.noApprovedProducts') }}</p>
          <f7-button outline raised round small color="primary" @click="goMarketplace">
            {{ $t('marketplace.browse') }}
          </f7-button>
        </div>

        <div v-else class="space-y-3">
          <OfferCard
            v-for="o in eligibleProducts"
            :key="o.id"
            :offer="o"
            compact
          >
            <template #actions>
              <f7-button fill round raised small class="display-flex align-items-center gap-1" @click="goCreate(o)">
                <f7-icon material="group_add" :size="14" />
                {{ $t('saveToBuy.saveTogether') }}
              </f7-button>
            </template>
          </OfferCard>
        </div>
      </f7-block>

      <!-- Demand Requests Section (Boîte à souhaits) -->
      <f7-block class="section-block">
        <div class="display-flex align-items-center justify-content-space-between mb-3">
          <div class="display-flex align-items-center gap-1.5">
            <f7-icon material="lightbulb" :size="20" class="text-secondary" />
            <h2 class="font-headline-sm text-on-surface font-bold text-base">{{ $t('saveToBuy.demandRequests') }}</h2>
          </div>
          <f7-button small round raised fill @click="goDemand" class="secondary-btn">
            {{ $t('saveToBuy.addWish') }}
          </f7-button>
        </div>

        <div v-if="!demands.length" class="empty-state">
          <p class="font-body-sm text-on-surface-variant">{{ $t('saveToBuy.demandIntro') }}</p>
        </div>

        <div v-else>
          <f7-list inset strong outline dividers class="demands-list">
            <f7-list-item
              v-for="r in demands"
              :key="r.id"
              class="demand-item"
            >
              <div class="demand-item-content display-flex align-items-center justify-content-space-between gap-3">
                <div class="min-w-0 flex-1">
                  <h4 class="font-headline-sm text-on-surface font-bold text-sm truncate">{{ r.requestedName }}</h4>
                  <p class="font-label-sm text-on-surface-variant mt-0.5">{{ demandSub(r) }}</p>
                </div>
                <f7-button fill round large raised class="display-flex align-items-center gap-1" @click="joinDemand(r)">
                  <f7-icon material="thumb_up" :size="14" />
                  <span>{{ $t('saveToBuy.join') }}</span>
                </f7-button>
              </div>
            </f7-list-item>
          </f7-list>
        </div>
      </f7-block>
    </div>
  </f7-page>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { f7 } from "framework7-vue";
import OfferCard from "../components/marketplace/OfferCard.vue";
import { fmtCfa } from "../constants/listing-options.js";
import {
  kindIsProduct,
  useMarketplaceStore,
} from "../js/pinia-stores/marketplace.js";
import {
  useSaveToBuyStore,
  CAMPAIGN_STATUS_META,
  PARTICIPANT_STATUS_META,
  tierUnitPrice,
} from "../js/pinia-stores/saveToBuy.js";
import { useGroupsStore } from "../js/pinia-stores/group.js";
import { useKeycloakStore } from "../js/pinia-stores/keycloak.js";
import i18n from "../js/i18n.js";

const props = defineProps({ groupId: { type: String, required: true } });

const { t } = i18n.global;
const saveToBuyStore = useSaveToBuyStore();
const marketplaceStore = useMarketplaceStore();
const groupsStore = useGroupsStore();
const keycloakStore = useKeycloakStore();

const loading = ref(true);

const groupCampaigns = computed(() => saveToBuyStore.byGroup(props.groupId));
const demands = computed(() =>
  saveToBuyStore.demandRequests.filter(
    (r) => String(r.sourceGroupId) === String(props.groupId),
  ),
);
const eligibleProducts = computed(() =>
  marketplaceStore
    .published(props.groupId)
    .filter((o) => kindIsProduct(o.kind)),
);
const myCommits = computed(() =>
  saveToBuyStore.myParticipations
    .map((p) => ({
      participant: p,
      campaign: saveToBuyStore.campaignById(p.campaignId),
    }))
    .filter((x) => !!x.campaign),
);

function statusMeta(c) {
  return CAMPAIGN_STATUS_META[c.status] || CAMPAIGN_STATUS_META.DRAFT;
}
function participantMeta(p) {
  return (
    PARTICIPANT_STATUS_META[p.status] || PARTICIPANT_STATUS_META.JOINED
  );
}
function statusBg(c) {
  return colorMix(statusMeta(c).color, 12);
}
function progressOf(c) {
  return Math.min(
    (Number(c.currentQuantity) / Number(c.targetQuantity || 1)) * 100,
    100,
  );
}
function priceLabel(c) {
  const unit = c.currentUnitPrice ?? tierUnitPrice(c.tiers, c.currentQuantity);
  return unit ? fmtCfa(unit) : "";
}
function demandSub(r) {
  const parts = [];
  if (r.targetUnitPrice)
    parts.push(`${t("saveToBuy.targetPrice")} ${fmtCfa(r.targetUnitPrice)}`);
  parts.push(t("saveToBuy.interestedCount", { count: r.interestedCount || 1 }));
  return parts.join(" · ");
}
function colorMix(hex, alpha) {
  const h = hex.replace("#", "");
  const bigint = parseInt(h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha / 100})`;
}

function campaignHref(c) {
  return `/groups/${props.groupId}/save-to-buy/campaigns/${c.id}`;
}
function goCreate(offer) {
  f7.views.main.router.navigate(`/groups/${props.groupId}/save-to-buy/create/${offer.id}`);
}
function goDemand() {
   f7.views.main.router.navigate(`/groups/${props.groupId}/save-to-buy/demand`);
}
function goMarketplace() {
   f7.views.main.router.navigate(`/groups/${props.groupId}/marketplace`);
}
async function joinDemand(demand) {
  try {
    await saveToBuyStore.joinDemandRequest(
      demand.id,
      keycloakStore.subject || "",
    );
    f7.toast.show({ text: t("saveToBuy.interestAdded"), closeTimeout: 1800 });
  } catch (error) {
    f7.dialog.alert(
      error?.response?.data?.message || t("saveToBuy.errorGeneric"),
    );
  }
}

onMounted(async () => {
  const groupId = props.groupId;
  loading.value = true;
  await Promise.all([
    groupsStore.fetchGroupDetail(groupId).catch(() => {}),
    marketplaceStore.refreshAll(groupId).catch(() => {}),
    saveToBuyStore.getMyParticipations(keycloakStore.subject || "").catch(() => {}),
  ]);
  await Promise.all([
    saveToBuyStore
      .loadCampaignsByGroup(groupId, marketplaceStore.published(groupId))
      .catch(() => {}),
    saveToBuyStore.loadGroupDemands(groupId).catch(() => {}),
  ]);
  loading.value = false;
});
</script>

<style scoped>
.save-to-buy-page {
  font-family: 'Work Sans', sans-serif;
}

/* Hero Card */
.hero-card {
  margin-bottom: 16px;
  overflow: hidden;
}
.hero-content {
  position: relative;
  z-index: 10;
  padding: 20px;
  background: linear-gradient(135deg, var(--avocash-green-light, #e8f0fe) 0%, var(--avocash-secondary-light, #fce4ec) 100%);
}
.hero-ambient {
  position: absolute;
  right: -24px;
  bottom: -24px;
  width: 96px;
  height: 96px;
  background: var(--avocash-primary, #0009ff);
  opacity: 0.06;
  border-radius: 50%;
  pointer-events: none;
}
.hero-badge {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--avocash-secondary-container, #fce4ec);
  color: var(--avocash-on-secondary-container, #880e4f);
  display: flex;
  align-items: center;
  justify-content: center;
}
.hero-desc {
  line-height: 1.5;
}
.hero-cta {
  --f7-button-bg-color: var(--avocash-primary, #0009ff);
  --f7-button-text-color: #fff;
  --f7-button-border-radius: 12px;
  padding: 10px 16px;
}

/* Section Blocks */
.section-block {
  margin-bottom: 24px;
}

/* Campaigns List */
.campaigns-list {
  --f7-list-inset-side-margin: 16px;
}
.campaign-item {
  --f7-list-item-height: auto;
  --f7-list-item-padding-horizontal: 16px;
  --f7-list-item-padding-vertical: 16px;
}
.campaign-item:after {
  display: none;
}
.campaign-item-content {
  width: 100%;
}
.status-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 999px;
  white-space: nowrap;
}
.campaign-progress {
  --f7-progressbar-height: 8px;
  --f7-progressbar-bg-color: var(--f7-list-item-border-color, rgba(0,0,0,0.08));
}
.campaign-progress .progressbar-fill {
  background: var(--avocash-primary, #0009ff);
  border-radius: 4px;
}

/* Commits List */
.commits-list {
  --f7-list-inset-side-margin: 16px;
}
.commit-item {
  --f7-list-item-height: auto;
  --f7-list-item-padding-horizontal: 16px;
  --f7-list-item-padding-vertical: 14px;
}
.commit-item:after {
  display: none;
}
.commit-item-content {
  width: 100%;
}

/* Demands List */
.demands-list {
  --f7-list-inset-side-margin: 16px;
}
.demand-item {
  --f7-list-item-height: auto;
  --f7-list-item-padding-horizontal: 16px;
  --f7-list-item-padding-vertical: 14px;
}
.demand-item:after {
  display: none;
}
.demand-item-content {
  width: 100%;
}

/* Empty States */
.empty-state {
  padding: 32px 16px;
  text-align: center;
  background: var(--f7-card-bg-color, #fff);
  border-radius: 16px;
  border: 1px solid var(--f7-list-item-border-color, rgba(0,0,0,0.08));
}

/* Secondary Button */
.secondary-btn {
  --f7-button-bg-color: transparent;
  --f7-button-text-color: var(--avocash-primary, #0009ff);
  --f7-button-border-color: var(--avocash-primary, #0009ff);
  --f7-button-border-width: 1px;
  font-weight: 600;
}
</style>